import { useEffect, useMemo, useRef, useState } from 'react';
import useTaxFormHighlightStore from '../stores/useTaxFormHighlight.store';

export const useTaxFormHighlight = (
  id: string | number,
  externalRef?: React.RefObject<HTMLDivElement | null>
) => {
  const highlightedTaxFormId = useTaxFormHighlightStore((state) => state.highlightedTaxFormId);
  const resetHighlight = useTaxFormHighlightStore((state) => state.resetHighlight);

  const isHighlighted = useMemo(() => String(id) === highlightedTaxFormId, [id, highlightedTaxFormId]);

  const localRef = useRef<HTMLDivElement | null>(null);
  const itemRef = externalRef || localRef;
  const [isVisualHighlightActive, setIsVisualHighlightActive] = useState(false);

  useEffect(() => {
    if (!isHighlighted) {
      setIsVisualHighlightActive(false);
      return;
    }

    const element = itemRef.current;
    if (!element) {
      if (typeof IntersectionObserver === 'undefined') {
        setIsVisualHighlightActive(true);
        const timer = setTimeout(() => {
          resetHighlight();
        }, 5000);
        return () => clearTimeout(timer);
      }
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisualHighlightActive(true);
          observer.disconnect();

          const timer = setTimeout(() => {
            resetHighlight();
          }, 5000);

          return () => clearTimeout(timer);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [isHighlighted, resetHighlight]);

  return { itemRef, isVisualHighlightActive };
};

export default useTaxFormHighlight;
